import { useAppDispatch, useAppSelector } from '@/app/hooks';
import { fetchAttendeeInfo, fetchRoleToken } from '@/features/live/liveThunks';
import { hydrateAttendeeInfoFromSubscriptionJwt } from '@/features/live/liveSlice';
import { useParams, useSearchParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { RootState } from '@/app/store';
import type { RoleToken } from '@/features/live/types';
import SpeakerLivePage from '@/pages/public/SpeakerLivePage';
import SplashScreen from '@/components/common/SplashScreen';
import AttendeeLiveGate from '@/pages/public/AttendeeLiveGate';
import {
  getPublicLiveJwtKind,
  parsePublicWebinarMeFromSubscriptionToken,
  subscriptionJwtHasFullAttendeeClaims,
} from '@/lib/publicLiveJwt';
import { decideLiveEntry, LIVE_ENTRY_TIMEOUT_MS } from '@/lib/liveEntryRouting';
import { useElapsedFlag } from '@/hooks/useElapsedFlag';
import LiveEntryError from '@/components/common/LiveEntryError';

function parseRoleFromRoleToken(roleToken: RoleToken | null) {
  const id = roleToken?.identity ?? '';
  const [, suffix] = id.split('-', 2);
  if (id.startsWith('presenter-')) return { role: 'presenter' as const, userId: suffix };
  if (id.startsWith('speaker-')) return { role: 'speaker' as const, userId: suffix };
  if (id.startsWith('attendee-')) return { role: 'attendee' as const, userId: suffix };
  return { role: undefined, userId: undefined };
}

export default function RoleToken() {
  const { webinarId: webinarIdParam, sessionIdentifier } = useParams<{
    webinarId: string;
    sessionIdentifier: string;
  }>();
  const [searchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const token = searchParams.get('token');
  const webinarId = webinarIdParam ? Number.parseInt(webinarIdParam, 10) : null;

  const { attendeeInfo, attendeeInfoLoading, attendeeInfoError } = useAppSelector(
    (s: RootState) => s.live,
  );
  const { roleToken, roleTokenLoading, roleTokenError } = useAppSelector((s: RootState) => s.live);

  const [hasTriedAttendeeInfo, setHasTriedAttendeeInfo] = useState(false);
  const [hasRequestedRoleToken, setHasRequestedRoleToken] = useState(false);

  useEffect(() => {
    if (!token || webinarId == null || hasTriedAttendeeInfo) return;

    const kind = getPublicLiveJwtKind(token);
    if (kind === 'INVITE') {
      setHasTriedAttendeeInfo(true);
      return;
    }

    if (attendeeInfoLoading) return;

    setHasTriedAttendeeInfo(true);

    if (subscriptionJwtHasFullAttendeeClaims(token)) {
      const me = parsePublicWebinarMeFromSubscriptionToken(token, webinarId);
      if (me) {
        dispatch(hydrateAttendeeInfoFromSubscriptionJwt(me));
        return;
      }
    }

    void dispatch(
      fetchAttendeeInfo({
        webinarId,
        authorization: token,
      }),
    );
  }, [token, webinarId, dispatch, hasTriedAttendeeInfo, attendeeInfoLoading]);

  useEffect(() => {
    if (!token || webinarId == null || !sessionIdentifier || hasRequestedRoleToken) return;

    if (getPublicLiveJwtKind(token) !== 'INVITE') {
      setHasRequestedRoleToken(true);
      return;
    }

    setHasRequestedRoleToken(true);
    void dispatch(
      fetchRoleToken({
        webinarId,
        sessionIdentifier,
        inviteToken: token,
      }),
    );
  }, [token, webinarId, sessionIdentifier, dispatch, hasRequestedRoleToken]);

  const { role, userId } = parseRoleFromRoleToken(roleToken);
  const isInviteFlow = Boolean(token) && getPublicLiveJwtKind(token as string) === 'INVITE';

  const entryState = {
    hasToken: Boolean(token),
    isInviteFlow,
    roleRequested: hasRequestedRoleToken,
    roleLoading: roleTokenLoading,
    roleError: Boolean(roleTokenError),
    role,
    hasUserId: Boolean(userId),
    attendeeTried: hasTriedAttendeeInfo,
    attendeeLoading: attendeeInfoLoading,
    attendeeError: Boolean(attendeeInfoError),
    hasAttendeeInfo: Boolean(attendeeInfo),
  };

  const stillLoading = decideLiveEntry({ ...entryState, timedOut: false }).kind === 'loading';
  const timedOut = useElapsedFlag(LIVE_ENTRY_TIMEOUT_MS, stillLoading);
  const decision = decideLiveEntry({ ...entryState, timedOut });

  switch (decision.kind) {
    case 'speaker':
      return <SpeakerLivePage role="speaker" userId={userId as string} />;
    case 'attendee':
      return <AttendeeLiveGate />;
    case 'error':
      return <LiveEntryError step={decision.step} />;
    default:
      return <SplashScreen />;
  }
}
